import createMarkersAPI from './markers-api';

const POI_MARKER_ID = 'pointOfInterest';

// Generate a GeoJSON circle polygon (64 points)
const createGeoJSONCircle = (center, radiusMeters, numPoints = 64) => {
  const coords = [];
  const earthRadius = 6371000; // meters
  const lat = (center.lat * Math.PI) / 180;
  const lng = (center.lng * Math.PI) / 180;
  const d = radiusMeters / earthRadius;

  for (let i = 0; i <= numPoints; i++) {
    const bearing = (2 * Math.PI * i) / numPoints;
    const pLat = Math.asin(
      Math.sin(lat) * Math.cos(d) +
        Math.cos(lat) * Math.sin(d) * Math.cos(bearing)
    );
    const pLng =
      lng +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat),
        Math.cos(d) - Math.sin(lat) * Math.sin(pLat)
      );
    coords.push([(pLng * 180) / Math.PI, (pLat * 180) / Math.PI]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    }
  };
};

export class PoiAPI {
  constructor(map, opts = {}) {
    const {
      markerIcon,
      circleColor,
      circleRadius,
      onMarkerClick,
      onMarkerMouseOver,
      onMarkerMouseOut,
      labelClasses
    } = opts;

    this.markerIcon = markerIcon;
    this.circleColor = circleColor;
    this.circleRadius = circleRadius;
    this.poiBoundaries = null;
    this.map = map;
    this.labelClasses = labelClasses;
    this.circles = {};

    this.markersAPI = createMarkersAPI(this.map, {
      markerIcon,
      onMarkerClick,
      onMarkerMouseOver,
      onMarkerMouseOut,
      labelClasses: this.labelClasses
    });
  }

  getPoiBoundaries = () => {
    return this.poiBoundaries;
  };

  addPointOfInterest = ({ lat, lng } = {}) => {
    if (lat && lng) {
      this.poiBoundaries = {
        lat,
        lng
      };

      this.markersAPI.removeMarkers([POI_MARKER_ID], { deleteMarker: true });

      this.markersAPI.addMarker(POI_MARKER_ID, {
        lat,
        lng,
        silent: true
      });

      this.addCircle(POI_MARKER_ID, {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        color: this.circleColor,
        radius: this.circleRadius
      });
    }
  };

  removePointOfInterest = () => {
    this.markersAPI.removeMarker(POI_MARKER_ID);
    this.markersAPI.deleteMarker(POI_MARKER_ID);
    this.removeCircle(POI_MARKER_ID);
    this.poiBoundaries = null;
  };

  addCircle = (circleId, opts) => {
    if (this.circles[circleId]) {
      this.removeCircle(circleId);
    }

    const sourceId = `poi-circle-${circleId}`;
    const layerId = `poi-circle-layer-${circleId}`;
    const outlineLayerId = `poi-circle-outline-${circleId}`;

    const geojson = createGeoJSONCircle(
      { lat: opts.lat, lng: opts.lng },
      opts.radius
    );

    const addLayers = () => {
      if (this.map.getSource(sourceId)) {
        this.map.getSource(sourceId).setData(geojson);
        return;
      }

      this.map.addSource(sourceId, {
        type: 'geojson',
        data: geojson
      });

      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': opts.color,
          'fill-opacity': 0.25
        }
      });

      this.map.addLayer({
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': opts.color,
          'line-width': 1,
          'line-opacity': 1
        }
      });
    };

    if (this.map.isStyleLoaded()) {
      addLayers();
    } else {
      this.map.on('load', addLayers);
    }

    this.circles[circleId] = { sourceId, layerId, outlineLayerId };
  };

  removeCircle = circleId => {
    const circle = this.circles[circleId];
    if (circle) {
      const { sourceId, layerId, outlineLayerId } = circle;

      if (this.map.getLayer(outlineLayerId)) {
        this.map.removeLayer(outlineLayerId);
      }
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }

      delete this.circles[circleId];
    }
  };
}

const createPoiAPI = (map, options) => {
  const poiAPI = new PoiAPI(map, options);

  return {
    addPointOfInterest: poiAPI.addPointOfInterest,
    removePointOfInterest: poiAPI.removePointOfInterest,
    getPoiBoundaries: poiAPI.getPoiBoundaries
  };
};

export default createPoiAPI;
